<template>
  <div class="launch-page" :class="themeClass" :lang="appStore.language">
    <div class="content-wrapper">
      <div class="slideshow">
        <div v-if="visibleGames.length" class="slide-container">
          <transition name="fade">
            <img
              :key="currentGameKey"
              :src="currentGame.image"
              class="slide"
              loading="eager"
              decoding="async"
              alt=""
            />
          </transition>
          <div class="game-info-overlay">
            <h2>{{ currentGameName }}</h2>
            <div v-if="currentGameDescription" ref="descriptionWindow" class="description-window">
              <p
                ref="descriptionText"
                :key="currentGameKey"
                :class="{ scrolling: descriptionOverflows }"
                :style="{ '--description-scroll': `-${descriptionScrollDistance}px` }"
              >{{ currentGameDescription }}</p>
              <span v-if="descriptionOverflows" class="description-ellipsis" aria-hidden="true">…</span>
            </div>
          </div>
        </div>
        <div v-else-if="gamesLoading" class="games-loading">
          <span class="loading-spinner" aria-hidden="true"></span>
          <span>{{ t('idle.loading_games') }}</span>
        </div>
        <div v-else class="no-games">{{ t('idle.no_games') }}</div>
      </div>

      <aside class="room-panel">
        <div class="qr-section">
          <p>{{ t('game.scan') }}</p>
          <div class="qr-box"><QrcodeVue :value="joinUrl" :size="qrSize" /></div>
          <strong>{{ appStore.name }}</strong>
        </div>
        <div class="players-section">
          <h2>{{ t('idle.connected_players', { count: connectedPlayers.length }) }}</h2>
          <div class="player-list">
            <div
              v-for="(player, index) in displayedPlayers"
              :key="player.id"
              class="player-card"
              :class="{ host: player.id === appStore.room?.creator }"
              :style="{ '--player-colour': playerColour(player, index) }"
            >
              <img v-if="playerImage(player)" :src="playerImage(player)" class="avatar" alt="" />
              <span v-else class="avatar initials">{{ initials(playerName(player)) }}</span>
              <span class="player-name">{{ playerName(player) }}</span>
              <span class="player-state">
                <span class="ready-badge" :class="{ visible: isGameSelected && player.ready }" :title="isGameSelected && player.ready ? t('idle.ready') : ''">✓</span>
                <span v-if="player.id === appStore.room?.creator" class="creator-badge">{{ t('idle.host') }}</span>
              </span>
            </div>
            <div v-if="additionalPlayerCount > 0" class="additional-players">
              <span class="additional-count">+{{ additionalPlayerCount }}</span>
              <span>{{ additionalPlayersMessage }}</span>
            </div>
          </div>
        </div>
      </aside>
    </div>

    <div class="message">
      <h1>{{ bottomMessage }}</h1>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, onBeforeUnmount, watch } from 'vue';
import { useAppStore } from '@/stores/appStore'
import { useWebsocketStore } from '@/stores/websocketStore'
import { useI18n } from 'vue-i18n'
import { db } from '@/plugins/firebase'
import { collection, getDocs } from 'firebase/firestore'
import QrcodeVue from 'qrcode.vue'

const appStore = useAppStore()
const socketStore = useWebsocketStore()
const { t } = useI18n()
const gamesLoading = ref(appStore.games.length === 0)
const isGameSelected = computed(() => appStore.viewState === 'join' || appStore.status === 'launched')
const isVisibleGame = (game) => game?.active === true
  && (String(appStore.environment || 'development').toLowerCase() !== 'production' || game.devOnly !== true)
  && Boolean(game.image)
const visibleGames = computed(() => (appStore.games || []).filter(isVisibleGame))
const connectedPlayers = computed(() => {
  const participants = new Map((appStore.room?.game?.players || []).map((player) => [player.id, player]))
  return (appStore.room?.players || []).map((player) => ({
    ...player,
    ready: participants.get(player.id)?.ready === true
  }))
})
const orderedPlayers = computed(() => {
  const players = [...connectedPlayers.value]
  const hostId = appStore.room?.creator
  return players.sort((left, right) => {
    if (left.id === hostId) return -1
    if (right.id === hostId) return 1
    return 0
  })
})
const displayedPlayers = computed(() => {
  if (orderedPlayers.value.length <= 4) return orderedPlayers.value
  const host = orderedPlayers.value.find((player) => player.id === appStore.room?.creator)
  return host ? [host] : []
})
const additionalPlayerCount = computed(() => connectedPlayers.value.length - displayedPlayers.value.length)
const additionalPlayersMessage = computed(() => additionalPlayerCount.value === 1
  ? t('idle.one_other_player')
  : t('idle.other_players', { count: additionalPlayerCount.value }))
const joinUrl = computed(() => `https://smeegoon.com/app/join?room=${encodeURIComponent(appStore.name || '')}`)
const qrSize = computed(() => Math.round(Math.min(window.innerWidth * 0.11, window.innerHeight * 0.18, 112)))
const bottomMessage = computed(() => {
  if (isGameSelected.value) return t('idle.waiting_until_ready')
  return connectedPlayers.value.length < 2
    ? t('idle.waiting_for_players')
    : t('launch.message')
})
const selectedGame = computed(() => appStore.room?.game?.id ? appStore.room.game : appStore.game)
const currentGame = computed(() => isGameSelected.value
  ? (selectedGame.value || {})
  : (visibleGames.value[currentIndex.value] || {}))
const currentGameKey = computed(() => currentGame.value.id || currentGame.value.image || currentIndex.value)
const currentGameName = computed(() => {
  const game = currentGame.value
  const name = game.name
  if (name && typeof name === 'object') {
    return name[appStore.language] || name.en || Object.values(name)[0] || ''
  }
  return game.title || name || ''
})
const currentGameDescription = computed(() => {
  const description = currentGame.value.description
  if (description && typeof description === 'object') {
    return description[appStore.language] || description.en || Object.values(description)[0] || ''
  }
  return description || ''
})
const descriptionWindow = ref(null)
const descriptionText = ref(null)
const descriptionOverflows = ref(false)
const descriptionScrollDistance = ref(0)

defineProps({
  theme: { type: String },
  language: { type: String, default: 'en' },
  user: { type: String },
  environment: { type: String }
})

const currentIndex = ref(0);
let slideshowInterval = null;
const imageCache = new Map()

function preloadImage(src) {
  if (!src) return Promise.resolve()
  if (imageCache.has(src)) return imageCache.get(src)

  const request = new Promise((resolve) => {
    const image = new Image()
    image.decoding = 'async'
    image.onload = resolve
    image.onerror = resolve
    image.src = src
  })
  imageCache.set(src, request)
  return request
}

async function advanceSlide() {
  if (isGameSelected.value) return
  const games = visibleGames.value
  if (games.length < 2) return
  const nextIndex = (currentIndex.value + 1) % games.length
  await preloadImage(games[nextIndex]?.image)
  currentIndex.value = nextIndex
}

async function measureDescription() {
  await nextTick()
  const viewport = descriptionWindow.value
  const text = descriptionText.value
  if (!viewport || !text) {
    descriptionOverflows.value = false
    descriptionScrollDistance.value = 0
    return
  }
  const distance = Math.max(0, Math.ceil(text.scrollHeight - viewport.clientHeight))
  descriptionScrollDistance.value = distance
  descriptionOverflows.value = distance > 2
}

watch(currentGameDescription, measureDescription, { flush: 'post' })

watch(isGameSelected, async (selected) => {
  if (selected) {
    clearInterval(slideshowInterval)
    slideshowInterval = null
    await preloadImage(selectedGame.value?.image)
  } else if (!slideshowInterval && visibleGames.value.length > 1) {
    slideshowInterval = setInterval(() => { void advanceSlide() }, 5000)
  }
  await measureDescription()
})

onMounted(async () => {
  if (appStore.games.length === 0) {
    try {
      const snapshot = await getDocs(collection(db, 'games'))
      const games = snapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
      appStore.games = games
    } catch (error) {
      console.error('[Idle] Unable to load games', error)
    } finally {
      gamesLoading.value = false
    }
  } else {
    gamesLoading.value = false
  }
  if (visibleGames.value.length > 0) {
    currentIndex.value = 0
    await preloadImage(visibleGames.value[0].image)
    gamesLoading.value = false
    visibleGames.value.slice(1).forEach((game) => { void preloadImage(game.image) })
    slideshowInterval = setInterval(() => { void advanceSlide() }, 5000)
  }
  window.addEventListener('resize', measureDescription)
  await measureDescription()
  if (!isGameSelected.value && socketStore.socket && socketStore.socket.readyState === WebSocket.OPEN) {
      socketStore.socket.send(JSON.stringify({
        event: 'idle',
        data: {
          creator: appStore.creator,
          environment: appStore.environment,
          players:appStore.players
        }
      }));
    }
});

onBeforeUnmount(() => {
  clearInterval(slideshowInterval);
  window.removeEventListener('resize', measureDescription)
});

const themeClass = computed(() => (appStore.theme === 'light' ? 'light' : 'dark'))
const playerName = (player) => player?.displayName || player?.name || player?.email || t('idle.player')
const playerImage = (player) => player?.avatar || player?.photoURL || player?.image || ''
const initials = (name) => name.trim().split(/\s+/).map((word) => word[0]).join('').slice(0, 2).toUpperCase()
const playerColours = ['#ffd65a', '#58b8ff', '#63dc91', '#f27b88']
const playerColour = (player, index) => player?.playerColour || playerColours[index % playerColours.length]

</script>

<style scoped>
.launch-page {
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100vw;
  overflow: hidden;
  justify-content: space-between;
  color: var(--idle-text);
  background: var(--idle-bg);
  font-family: 'Noto Sans', sans-serif;
}

.launch-page.dark {
  --idle-bg: #000;
  --idle-text: #fff;
  --idle-muted: #aaa;
  --idle-spinner-track: rgba(255, 255, 255, 0.25);
  --idle-accent: #fff7b2;
  --idle-panel: #121212;
  --idle-card: #202020;
}

.launch-page.light {
  --idle-bg: #fff;
  --idle-text: #000;
  --idle-muted: #555;
  --idle-spinner-track: rgba(0, 0, 0, 0.2);
  --idle-accent: #cd7372;
  --idle-panel: #f0f0f0;
  --idle-card: #fff;
}

.content-wrapper {
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(250px, 2fr);
  flex: 1;
  min-height: 0;
  padding: 20px;
  gap: 20px;
}

.slideshow {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  background: transparent;
  border-radius: 24px;
}

.room-panel { min-width:0;display:flex;flex-direction:column;gap:1.5vh;padding:1.5vh 1.2vw;border-radius:24px;background:var(--idle-panel); }
.qr-section{text-align:center;flex:0 0 auto}.qr-section p{margin:0 0 .6vh;font-size:clamp(.8rem,2vh,1.15rem);font-weight:700}.qr-section strong{display:block;margin-top:.4vh;font-size:clamp(.8rem,2.2vh,1.2rem);letter-spacing:.12em}.qr-box{display:inline-flex;padding:7px;border-radius:12px;background:#fff}.players-section{min-height:0;display:flex;flex-direction:column}.players-section h2{margin:0 0 1vh;font-size:clamp(.9rem,2.3vh,1.3rem)}.player-list{min-height:0;overflow:hidden;display:grid;gap:.7vh}.player-card{display:grid;grid-template-columns:auto minmax(0,1fr) auto;align-items:center;gap:.7vw;padding:.65vh .65vw;border-radius:12px;background:var(--idle-card)}.avatar{--avatar-size:clamp(1.8rem,5.5vh,3.2rem);width:var(--avatar-size);height:var(--avatar-size);min-width:var(--avatar-size);border-radius:50%;object-fit:cover;box-sizing:border-box;overflow:hidden}.initials{display:grid;place-items:center;color:#fff;background:#52617a;font-weight:800}.player-name{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-weight:700}.creator-badge{padding:.25rem .45rem;border-radius:999px;background:var(--idle-accent);color:#171717;font-size:clamp(.6rem,1.5vh,.8rem);font-weight:800}
.player-card{border:3px solid var(--player-colour);box-shadow:0 0 .45rem var(--player-colour)}.player-card .avatar{border:2px solid var(--player-colour)}.additional-players{min-height:clamp(3.2rem,9vh,5rem);display:flex;align-items:center;justify-content:center;gap:.8rem;padding:1vh 1vw;border:2px dashed var(--idle-muted);border-radius:12px;text-align:center;font-weight:700}.additional-count{flex:none;width:clamp(2.2rem,6vh,3.4rem);aspect-ratio:1;display:grid;place-items:center;border-radius:50%;background:var(--idle-accent);color:#171717;font-size:clamp(.9rem,2.5vh,1.35rem);font-weight:900}
.player-state{display:flex;align-items:center;gap:.4rem}
.ready-badge{--badge-size:clamp(1.25rem,3.5vh,1.8rem);display:grid;place-items:center;width:var(--badge-size);height:var(--badge-size);min-width:var(--badge-size);min-height:var(--badge-size);box-sizing:border-box;border-radius:50%;background:#35c76f;color:#07170d;font-weight:900;line-height:1;visibility:hidden;opacity:0}.ready-badge.visible{visibility:visible;opacity:1}

.slide-container {
  height: 100%;
  width: auto;
  max-width: 100%;
  aspect-ratio: 4 / 3;
  position: relative;
}

.slide {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: var(--idle-bg);
  border-radius: 24px;
}

.game-info-overlay {
  position: absolute;
  z-index: 3;
  bottom: 0;
  left: 0;
  right: 0;
  padding: clamp(0.8rem, 3vh, 1.6rem) clamp(1rem, 2.5vw, 2rem);
  background: linear-gradient(transparent, rgba(0,0,0,0.8));
  color: white;
  text-align: left;
  border-bottom-left-radius: 24px;
  border-bottom-right-radius: 24px;
}

.game-info-overlay h2 {
  margin: 0;
  font-size: clamp(1rem, 3.4vh, 1.75rem);
  line-height: 1.12;
  overflow-wrap: anywhere;
}

.description-window {
  position: relative;
  height: 3.75em;
  margin-top: 0.45rem;
  overflow: hidden;
}

.game-info-overlay p {
  margin: 0.45rem 0 0;
  margin: 0;
  font-size: clamp(0.72rem, 1.8vh, 1rem);
  font-family: inherit;
  font-style: normal;
  font-weight: 400;
  font-synthesis: none;
  line-height: 1.25;
  opacity: 0.88;
  overflow-wrap: anywhere;
}

.game-info-overlay p.scrolling {
  animation: description-scroll 4.6s ease-in-out both;
}

.description-ellipsis {
  position: absolute;
  right: 0;
  bottom: 0;
  padding-left: 0.35em;
  color: white;
  background: linear-gradient(90deg, transparent, rgba(0,0,0,.82) 35%);
  font-size: clamp(0.72rem, 1.8vh, 1rem);
  line-height: 1.25;
  animation: ellipsis-hide 4.6s ease-in-out both;
}

.message {
  flex: 0 0 clamp(64px, 13vh, 82px);
  height: clamp(64px, 13vh, 82px);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  text-align: center;
  color: var(--idle-text);
  background: var(--idle-bg);
}

.message h1{margin:0;padding:0 2vw;font-size:clamp(1.1rem,3.5vh,2rem)}

@media (max-height: 600px), (max-width: 1000px) {
  .content-wrapper {
    grid-template-columns: minmax(0, 3fr) minmax(220px, 2fr);
    padding: 10px;
    gap: 10px;
  }

  .slideshow,
  .slide,
  .room-panel {
    border-radius: 16px;
  }

  .room-panel {
    gap: 0.7vh;
    padding: 1vh 0.8vw;
  }

  .qr-section p {
    margin-bottom: 0.25vh;
    font-size: clamp(0.65rem, 1.7vh, 0.85rem);
  }

  .qr-section strong {
    margin-top: 0.15vh;
    font-size: clamp(0.7rem, 1.8vh, 0.9rem);
  }

  .qr-box {
    padding: 4px;
    border-radius: 8px;
  }

  .players-section h2 {
    margin-bottom: 0.45vh;
    font-size: clamp(0.72rem, 1.9vh, 1rem);
  }

  .player-list {
    gap: 0.4vh;
  }

  .player-card {
    gap: 0.5vw;
    padding: 0.35vh 0.5vw;
    border-width: 2px;
    border-radius: 9px;
  }

  .avatar {
    --avatar-size: clamp(1.45rem, 4.6vh, 2rem);
  }

  .player-name {
    font-size: clamp(0.68rem, 1.8vh, 0.9rem);
  }

  .creator-badge {
    padding: 0.12rem 0.3rem;
    font-size: clamp(0.52rem, 1.25vh, 0.68rem);
  }

  .additional-players {
    min-height: 2.6rem;
    padding: 0.5vh 0.6vw;
    font-size: clamp(0.65rem, 1.7vh, 0.85rem);
  }

  .additional-count {
    width: clamp(1.8rem, 5vh, 2.4rem);
  }

  .game-info-overlay h2 {
    font-size: clamp(0.95rem, 3vh, 1.35rem);
  }

  .game-info-overlay p {
    font-size: clamp(0.6rem, 1.45vh, 0.78rem);
    line-height: 1.18;
  }

  .description-window {
    height: 2.36em;
    margin-top: 0.25rem;
  }

  .description-ellipsis {
    font-size: clamp(0.6rem, 1.45vh, 0.78rem);
    line-height: 1.18;
  }

  .message h1 {
    font-size: clamp(0.9rem, 2.8vh, 1.35rem);
  }
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 1s ease;
}
.fade-enter-active { z-index: 2; }
.fade-leave-active { z-index: 1; }
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

.no-games {
  font-size: 1.5rem;
  color: var(--idle-muted);
}

.games-loading {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  color: var(--idle-text);
  font-size: 1.25rem;
}

.loading-spinner {
  width: 2rem;
  aspect-ratio: 1;
  border: 0.28rem solid var(--idle-spinner-track);
  border-top-color: var(--idle-accent);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@keyframes description-scroll {
  0%, 28% { transform: translateY(0); }
  88%, 100% { transform: translateY(var(--description-scroll)); }
}

@keyframes ellipsis-hide {
  0%, 25% { opacity: 1; }
  35%, 100% { opacity: 0; }
}

@media (prefers-reduced-motion: reduce) {
  .game-info-overlay p.scrolling,
  .description-ellipsis { animation: none; }
}
</style>
